import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCw, XCircle, Download, Plus, FileText, FileCode2, FileSignature } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "../../lib/api";
import {
  BackLink,
  HeroStatus,
  Section,
  Card,
  CardTitle,
  ProgressCard,
  KvGrid,
  KvItem,
  FooterActions,
  ActionButton,
  TechDetailsToggle,
  Alert,
  fmtItalianDateTime,
  timeAgo,
  useNow,
  upperOrDash,
  EM_DASH,
} from "./Primitives";

const upperFirst = (s) => (s && typeof s === "string" ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const fmtKB = (b) => (b ? `${Math.max(1, Math.round(b / 1024))} KB` : EM_DASH);

const downloadSlot = async (requestId, slot, fallbackName) => {
  if (!requestId || String(requestId).startsWith("preview-")) {
    toast.info("Anteprima: download non disponibile sui mock");
    return;
  }
  try {
    const token = localStorage.getItem("wattline_token");
    const res = await fetch(`${API}/requests/${requestId}/pdf?file=${slot}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      let detail = "Errore download file";
      try { const j = await res.json(); if (j?.detail) detail = j.detail; } catch { /* */ }
      toast.error(detail);
      return;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fallbackName;
    a.click();
    URL.revokeObjectURL(url);
  } catch (e) {
    toast.error(`Errore download: ${e?.message || e}`);
  }
};

const SlotCard = ({ icon: Icon, title, description, sizeKB, onDownload, variant = "primary", testId, disabled, disabledLabel }) => {
  const isPrimary = variant === "primary";
  const isCompact = variant === "compact";
  return (
    <div
      data-testid={testId}
      className={`bg-surface border rounded-xl flex items-center gap-5 ${isPrimary ? "border-accent" : "border-border"}`}
      style={{ padding: isCompact ? 18 : 26 }}
    >
      <div
        className={`flex items-center justify-center flex-shrink-0 ${isPrimary ? "bg-accent text-white" : "bg-accent-subtle text-accent-strong"}`}
        style={{
          width: isCompact ? 40 : 52,
          height: isCompact ? 40 : 52,
          borderRadius: 10,
        }}
      >
        <Icon size={isCompact ? 18 : 22} strokeWidth={1.75} />
      </div>
      <div className="flex-1 min-w-0">
        <div className={`font-semibold text-text-primary truncate ${isPrimary ? "text-[16px]" : "text-[14px]"}`}>{title}</div>
        <div className="text-[12px] text-text-secondary mt-0.5 truncate">
          {description}
          {sizeKB ? ` · ${sizeKB}` : ""}
        </div>
      </div>
      <button
        type="button"
        onClick={onDownload}
        disabled={disabled}
        data-testid={`${testId}-download`}
        className={`inline-flex items-center gap-1.5 rounded-md font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
          isPrimary
            ? "bg-brand hover:bg-brand-hover text-white"
            : "bg-surface hover:bg-subtle text-text-primary border border-border-strong"
        }`}
        style={{
          padding: isPrimary ? "11px 18px" : "8px 14px",
          fontSize: isPrimary ? 14 : 13,
        }}
      >
        <Download size={isPrimary ? 15 : 13} />
        {disabled ? (disabledLabel || "Non disponibile") : "Scarica"}
      </button>
    </div>
  );
};

export const ResultBilancio = ({ request: initial, onUpdate, onNewSearch }) => {
  const navigate = useNavigate();
  const [request, setRequest] = useState(initial);
  useEffect(() => setRequest(initial), [initial]);

  useNow(10000);

  // Polling auto ogni 10s se pending
  useEffect(() => {
    const isPending = request?.status === "pending_external" || request?.status === "in_progress";
    if (!isPending || !request?.id || String(request.id).startsWith("preview-")) return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/requests/${request.id}`);
        setRequest(data);
        if (onUpdate) onUpdate(data);
      } catch { /* swallow */ }
    }, 10000);
    return () => clearInterval(t);
  }, [request?.id, request?.status, onUpdate]);

  const onManualRefresh = async () => {
    if (!request?.id || String(request.id).startsWith("preview-")) return;
    try {
      const { data } = await api.get(`/requests/${request.id}`);
      setRequest(data);
      if (onUpdate) onUpdate(data);
    } catch { /* swallow */ }
  };

  const piva = request?.params?.cf_piva_id || EM_DASH;
  const annoParam = request?.params?.anno_chiusura;
  const annoRichiesto = annoParam && annoParam !== "ultimo" ? annoParam : "Ultimo disponibile";
  const annoChiusura = request?.response_data?.anno_chiusura || annoParam;

  /* ----------------------- IN-PROGRESS ----------------------- */
  if (request?.status === "pending_external" || request?.status === "in_progress" || request?.status === "pending_confirmation") {
    const since = timeAgo(request?.created_at);
    return (
      <div className="max-w-3xl" data-testid="result-bilancio-in-progress">
        <BackLink to="/history" label="Storico richieste" />

        <HeroStatus
          variant="info"
          title="Bilancio in elaborazione"
          description="La Camera di Commercio sta preparando i documenti del bilancio · evasione fino a 15 minuti"
          testId="result-hero"
        />

        <Section>
          <ProgressCard
            title="Attesa risposta dalla Camera di Commercio"
            description={`Richiesta inviata ${since}. Tempi medi: 1–15 minuti. Riceverai bilancio, verbale e XBRL appena pronti.`}
          />
        </Section>

        <Section>
          <Card>
            <CardTitle>Dati richiesta</CardTitle>
            <KvGrid columns={2}>
              <KvItem label="P.IVA / C.F. azienda" value={upperOrDash(piva)} mono />
              <KvItem label="Anno chiusura richiesto" value={annoRichiesto} />
              <KvItem
                label="Stato"
                value={
                  <span className="text-warning-strong font-medium">
                    {request?.openapi_state ? upperFirst(request.openapi_state) : "In ricerca"}
                  </span>
                }
              />
              <KvItem label="Richiesta inviata" value={fmtItalianDateTime(request?.created_at)} />
            </KvGrid>
          </Card>
        </Section>

        <FooterActions>
          <ActionButton variant="outline" icon={RefreshCw} onClick={onManualRefresh} testId="action-refresh">
            Aggiorna stato
          </ActionButton>
          <ActionButton variant="ghost" icon={XCircle} onClick={() => {}} testId="action-cancel">
            Annulla richiesta
          </ActionButton>
        </FooterActions>

        <TechDetailsToggle data={request} />
      </div>
    );
  }

  /* ----------------------- COMPLETED ----------------------- */
  if (request?.status === "completed") {
    const completedAt = request?.completed_at || request?.created_at;
    const attachments = request?.bilancio_attachments || {};
    const hasBilancio = !!attachments.bilancio;
    const hasVerbale = !!attachments.verbale;
    const hasXbrl = !!attachments.xbrl;

    const bilancioFn = attachments.bilancio?.filename || `bilancio_${request.id?.slice(0, 8)}.pdf`;
    const verbaleFn = attachments.verbale?.filename || `verbale_${request.id?.slice(0, 8)}.pdf`;
    const xbrlFn = attachments.xbrl?.filename || `bilancio_${request.id?.slice(0, 8)}.xbrl`;

    return (
      <div className="max-w-3xl" data-testid="result-bilancio-completed">
        <BackLink to="/history" label="Storico richieste" />

        <HeroStatus
          variant="success"
          title="Bilancio disponibile"
          description="Documenti ufficiali depositati presso Camera di Commercio · pronti per il download"
          testId="result-hero"
        />

        <Section>
          <Card>
            <CardTitle>Documenti del bilancio</CardTitle>
            <div className="space-y-3">
              <SlotCard
                icon={FileText}
                title="Bilancio di Esercizio"
                description={hasBilancio ? "PDF ufficiale depositato in CCIAA" : "PDF non incluso nella risposta"}
                sizeKB={fmtKB(attachments.bilancio?.size_bytes)}
                onDownload={() => downloadSlot(request.id, "bilancio", bilancioFn)}
                variant="primary"
                testId="bilancio-slot-bilancio"
                disabled={!hasBilancio}
              />
              <SlotCard
                icon={FileSignature}
                title="Verbale Assemblea Soci"
                description={hasVerbale ? "PDF del verbale di approvazione bilancio" : "Verbale non disponibile per questo esercizio"}
                sizeKB={fmtKB(attachments.verbale?.size_bytes)}
                onDownload={() => downloadSlot(request.id, "verbale", verbaleFn)}
                variant="secondary"
                testId="bilancio-slot-verbale"
                disabled={!hasVerbale}
              />
              <SlotCard
                icon={FileCode2}
                title="XBRL (dati strutturati)"
                description={hasXbrl ? "Formato avanzato per integrazione gestionali e analisi automatiche" : "XBRL non disponibile per questo esercizio"}
                sizeKB={fmtKB(attachments.xbrl?.size_bytes)}
                onDownload={() => downloadSlot(request.id, "xbrl", xbrlFn)}
                variant="compact"
                testId="bilancio-slot-xbrl"
                disabled={!hasXbrl}
              />
            </div>
          </Card>
        </Section>

        <Section>
          <Card>
            <CardTitle>Dati richiesta</CardTitle>
            <KvGrid columns={2}>
              <KvItem label="P.IVA azienda" value={upperOrDash(piva)} mono testId="kv-piva" />
              <KvItem label="Anno chiusura" value={annoChiusura ? String(annoChiusura) : annoRichiesto} testId="kv-anno" />
              <KvItem
                label="Stato richiesta"
                value={
                  <span className="text-success font-medium">
                    {request?.openapi_state ? upperFirst(request.openapi_state) : "Dati disponibili"}
                  </span>
                }
                testId="kv-stato"
              />
              <KvItem label="Documenti generati" value={fmtItalianDateTime(completedAt)} testId="kv-completed" />
            </KvGrid>
          </Card>
        </Section>

        <Section>
          <Alert variant="info" testId="bilancio-disclaimer">
            <strong>Documento ufficiale.</strong> I file ricevuti sono copia del bilancio depositato presso la Camera di Commercio.
            Il formato <em>XBRL</em> contiene i dati strutturati machine-readable, utili per analisi finanziarie automatizzate.
          </Alert>
        </Section>

        <FooterActions>
          <ActionButton variant="primary" icon={Plus} onClick={() => (onNewSearch ? onNewSearch() : navigate("/search/bilancio"))} testId="action-new">
            Nuovo bilancio
          </ActionButton>
        </FooterActions>

        <TechDetailsToggle data={request} />
      </div>
    );
  }

  /* ----------------------- FAILED ----------------------- */
  return (
    <div className="max-w-3xl" data-testid="result-bilancio-failed">
      <BackLink to="/history" label="Storico richieste" />
      <HeroStatus
        variant="danger"
        title="Richiesta bilancio fallita"
        description={request?.error_message || "Errore non specificato"}
      />
      <Section>
        <Card>
          <CardTitle>Dati richiesta</CardTitle>
          <KvGrid columns={2}>
            <KvItem label="P.IVA azienda" value={upperOrDash(piva)} mono />
            <KvItem label="Anno chiusura richiesto" value={annoRichiesto} />
            <KvItem label="Stato esterno" value={request?.openapi_state || EM_DASH} />
            <KvItem label="Errore" value={request?.error_message || EM_DASH} />
          </KvGrid>
        </Card>
      </Section>
      <FooterActions>
        <ActionButton variant="outline" icon={Plus} onClick={() => (onNewSearch ? onNewSearch() : navigate("/search/bilancio"))} testId="action-new">
          Nuova ricerca
        </ActionButton>
      </FooterActions>
      <TechDetailsToggle data={request} />
    </div>
  );
};
